import axios from 'axios';
import { AppSettings } from '../settings/types';
import xeroAuth from './xeroAuth';
import { fetchDudaSiteAnalytics, listDudaSites, parseDudaActivityVisits } from './dudaApi';
import { fetchStripeBalance } from './stripeApi';

export async function testStripeConnection(secretKey: string): Promise<{ ok: true; label: string } | { ok: false; message: string }> {
  const key = secretKey.trim();
  if (!key) {
    return { ok: false, message: 'Secret key is required' };
  }
  try {
    const data = await fetchStripeBalance(key);
    const cur = data?.available?.[0];
    const label = cur ? `Connected · ${cur.currency.toUpperCase()} balance available` : 'Connected';
    return { ok: true, label };
  } catch (e) {
    const msg = axios.isAxiosError(e) ? e.response?.data?.error?.message || e.message : 'Request failed';
    return { ok: false, message: String(msg) };
  }
}

export async function testXeroConnection(settings: AppSettings): Promise<{ ok: true; label: string } | { ok: false; message: string }> {
  const { clientId, clientSecret } = settings.integrations.xero;
  if (!clientId.trim() || !clientSecret.trim()) {
    return { ok: false, message: 'Client ID and Client Secret are required' };
  }
  try {
    await xeroAuth.ensureValidAccessToken();
    if (!xeroAuth.isAuthenticated()) {
      return { ok: false, message: 'Not connected — use “Connect Xero” first, then test again' };
    }
    const token = xeroAuth.getAccessToken();
    const { data } = await axios.get<Array<{ tenantName?: string }>>('https://api.xero.com/connections', {
      headers: { Authorization: `Bearer ${token}` },
    });
    const name = data[0]?.tenantName;
    return { ok: true, label: name ? `Connected · ${name}` : 'Connected' };
  } catch (e) {
    const msg = axios.isAxiosError(e) ? e.response?.data?.Detail || e.response?.data?.message || e.message : 'Request failed';
    return { ok: false, message: String(msg) };
  }
}

export async function testDudaConnection(
  apiUser: string,
  apiPassword: string,
  siteIdOrName?: string
): Promise<{ ok: true; label: string } | { ok: false; message: string }> {
  if (!apiUser.trim() || !apiPassword.trim()) {
    return { ok: false, message: 'API user and password are required' };
  }

  if (siteIdOrName?.trim()) {
    try {
      const data = await fetchDudaSiteAnalytics(apiUser, apiPassword, siteIdOrName.trim(), {
        result: 'activities',
        dateGranularity: 'MONTHS',
      });
      const visits = parseDudaActivityVisits(data);
      return {
        ok: true,
        label:
          visits != null
            ? `Analytics OK · parsed activity total ≈ ${visits}`
            : 'Analytics OK (credentials work — check Raw JSON on Duda dashboard for shape)',
      };
    } catch (e) {
      const msg = axios.isAxiosError(e)
        ? e.message === 'Network Error'
          ? 'Network error — run npm start (dev proxy) or add a server proxy for production'
          : e.response?.data?.message || e.response?.statusText || e.message
        : 'Request failed';
      return { ok: false, message: String(msg) };
    }
  }

  try {
    const sites = await listDudaSites(apiUser, apiPassword);
    return {
      ok: true,
      label: sites.length
        ? `Connected · ${sites.length} site(s) listed`
        : 'Connected · 0 sites (no sites in Partner API, or all are “In Planning” — those are excluded from list)',
    };
  } catch (e) {
    const msg = axios.isAxiosError(e)
      ? e.message === 'Network Error'
        ? 'Network error — use dev server proxy (/duda-api) or backend'
        : e.response?.data?.message || e.message
      : 'Request failed';
    return { ok: false, message: String(msg) };
  }
}

export async function testGoogleAdsConnection(integration: AppSettings['integrations']['googleAds']): Promise<
  { ok: true; label: string } | { ok: false; message: string }
> {
  const { developerToken, clientId, customerId } = integration;
  if (!developerToken.trim() || !clientId.trim() || !customerId.trim()) {
    return { ok: false, message: 'Developer token, OAuth client ID, and Customer ID are required for a full setup' };
  }
  return {
    ok: true,
    label: 'Fields saved — Google Ads API calls need a server-side OAuth token (next step for production)',
  };
}
